import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { fetchCart, clearCart } from '../../store/slices/cartSlice';
import { getPointsBalance } from '../../store/slices/pointsSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import styled from 'styled-components';
import { authAPI } from '../../api/authAPI';
import { updateUser } from '../../store/slices/authSlice';
import LocationPicker from '../../components/common/LocationPicker';
import {
  trackCheckoutStarted,
  trackOrderCompleted,
  trackItemSold,
  trackPaintingCustomization,
} from '../../utils/analytics';
import { ordersAPI } from '../../api/ordersAPI';
import { cartAPI } from '../../api/cartAPI';
import { itemsAPI } from '../../api/itemsAPI';
import { formatItemPriceEgp, formatWalletBalanceEgp } from '../../utils/formatPrice';
import { splitUserName } from '../../utils/userName';
import {
  POST_CHECKOUT_CART_SNAPSHOT_KEY,
  POST_CHECKOUT_SELLER_CATALOG_KEY,
  snapshotCartLinesForPostCheckoutReco,
  buildPostCheckoutSellerCatalogRecommendations,
} from '../../utils/postCheckoutSellerCatalog';
import { deriveCheckoutPricing } from '../../utils/checkoutPricing';
import { resolvePaymobCheckoutUrl } from '../../utils/paymobCheckout';
import { notifyCartStockAdjustments } from '../../utils/cartStockAdjustments';

const PENDING_FEE_INTENT_KEY = 'flea_pending_order_fee_intent_id';
const INSTAPAY_PENDING_KEY = 'flea_instapay_pending_payment';
const INSTAPAY_QR_URL = 'https://ipn.eg/S/ranakhamiss/instapay/5IYZ56';

const CheckoutContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;
`;

const DeliveryNotice = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px solid #bbf7d0;
`;

const DeliveryNoticeIcon = styled.span`
  font-size: 1.25rem;
  line-height: 1.4;
  flex-shrink: 0;
`;

const DeliveryNoticeBody = styled.div`
  min-width: 0;
`;

const DeliveryNoticeTitle = styled.div`
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #166534;
`;

const DeliveryNoticeText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: #166534;
`;

const CheckoutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const OrderSummary = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
`;

const SummaryTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  color: var(--text-secondary);
  
  &.total {
    font-weight: 700;
    font-size: 1.125rem;
    color: var(--text-primary);
    border-top: 1px solid var(--border-color);
    padding-top: 0.75rem;
    margin-top: 0.75rem;
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 1rem;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:disabled {
    background-color: var(--text-light);
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  width: 100%;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 0.75rem;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: var(--border-color);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  resize: vertical;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.95rem;
`;

const DeliveryOption = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 2px solid ${props => props.selected ? '#007AFF' : '#e0e0e0'};
  border-radius: 12px;
  background-color: ${props => props.selected ? '#f0f8ff' : '#fff'};
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  margin-bottom: 0.75rem;
  text-align: left;
  opacity: ${props => (props.$dimmed ? 0.45 : 1)};
  
  &:hover {
    border-color: ${props => (props.$dimmed ? '#e0e0e0' : '#007AFF')};
  }
`;

const Checkmark = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 14px;
  background-color: #007AFF;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-weight: bold;
  margin-left: 12px;
`;

const RemovalRecommendationCard = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const RemovalRecommendationActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const RemovalSecondaryButton = styled.button`
  padding: 0.45rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.8125rem;
  cursor: pointer;
`;

const RemovalPrimaryButton = styled.button`
  padding: 0.45rem 0.75rem;
  border-radius: 0.375rem;
  border: none;
  background: var(--primary-color);
  color: white;
  font-size: 0.8125rem;
  cursor: pointer;
`;

function buildDeliveryRemovalMessage(rec, cartItems) {
  if (!rec) return '';
  const idSet = new Set(rec.cart_item_ids_to_remove || []);
  const touched = (cartItems || []).filter((it) => idSet.has(it.cart_item_id));
  const titles = touched.map((it) => it.title || 'Item').filter(Boolean);
  const save = rec.savings_egp;
  const hyp = rec.hypothetical_delivery_fee_egp;
  if (rec.remove_mode === 'seller_lines' && (rec.seller_lines_in_cart_before || 0) >= 2) {
    const n = rec.lines_removed_count || titles.length || rec.cart_item_ids_to_remove.length;
    return `Removing all ${n} items from this seller would save you ${save} EGP on delivery (delivery would be ${hyp} EGP).`;
  }
  const name = titles[0] || 'This item';
  return `Removing ${name} would save you ${save} EGP on delivery (delivery would be ${hyp} EGP).`;
}

const FEE_PAYMENT_OPTIONS = [
  { id: 'instapay', label: 'Instapay', hint: 'Bank transfer · no processing fee', icon: '🏦' },
  { id: 'card', label: 'Credit / debit card', hint: 'Visa, Mastercard, Meeza', icon: '💳' },
  { id: 'google_pay', label: 'Google Pay', hint: 'Secure checkout via Paymob', icon: 'G' },
  { id: 'apple_pay', label: 'Apple Pay', hint: 'Secure checkout via Paymob', icon: '' },
  { id: 'valu', label: 'Valu', hint: 'Installments (via Paymob)', icon: '📊' },
  { id: 'cash', label: 'Cash', hint: 'Cash-friendly checkout flow', icon: '💵' },
];

const PaymentMethodsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const WalletToggleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 0.75rem;
  border: 2px solid var(--border-color);
  background: var(--bg-secondary);
  margin-top: 0.75rem;
`;

const WalletToggleMain = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  min-width: 0;
`;

const WalletIconWrap = styled.div`
  font-size: 1.75rem;
  line-height: 1;
  flex-shrink: 0;
`;

const FeePayHint = styled.p`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0.5rem 0 0;
  line-height: 1.45;
`;

const WalletCoverageWarning = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 0.9rem;
  border-radius: 0.5rem;
  border: 1px solid #f59e0b;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.85rem;
  font-weight: 600;
`;

const COORDINATE_ADDRESS_REGEX = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;

const parseCoordinateAddress = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.match(COORDINATE_ADDRESS_REGEX);
  if (!match) return null;
  return { latitude: Number(match[1]), longitude: Number(match[2]) };
};

const reverseGeocodeAddress = async ({ latitude, longitude }, apiKey) => {
  if (!apiKey) return null;
  try {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURIComponent(
      `${latitude},${longitude}`
    )}&key=${encodeURIComponent(apiKey)}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data?.status === 'OK' && data.results?.[0]?.formatted_address) {
      return data.results[0].formatted_address;
    }
  } catch (_error) {
    // Fallback handled by caller.
  }
  return null;
};

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, total, loading: cartLoading } = useSelector((state) => state.cart);
  const { balance, loading: pointsLoading } = useSelector((state) => state.points);
  const { error: orderError } = useSelector((state) => state.orders);
  const { user } = useSelector((state) => state.auth);
  const [checkingOut, setCheckingOut] = useState(false);
  const [completingAfterFees, setCompletingAfterFees] = useState(false);
  const [feeConfigOnline, setFeeConfigOnline] = useState(null);
  const [feeConfigInstapay, setFeeConfigInstapay] = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [feesError, setFeesError] = useState(null);
  const [feesFetchKey, setFeesFetchKey] = useState(0);
  const completionLockRef = useRef(false);
  const orderJustCompletedRef = useRef(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [shippingAddress, setShippingAddress] = useState('');
  const [addressCoords, setAddressCoords] = useState({ latitude: null, longitude: null });
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [displayShippingAddress, setDisplayShippingAddress] = useState('');
  const [removalRecommendationDismissed, setRemovalRecommendationDismissed] = useState(false);
  const [removingRecommendedLines, setRemovingRecommendedLines] = useState(false);
  const [feePaymentMethod, setFeePaymentMethod] = useState('instapay');
  const [useWalletForPoints, setUseWalletForPoints] = useState(true);

  const cartFeeFingerprint = useMemo(
    () =>
      (items || [])
        .map((it) => `${it.cart_item_id}:${it.quantity}`)
        .sort()
        .join('|'),
    [items]
  );

  useEffect(() => {
    // Fetch cart and points balance on mount
    dispatch(fetchCart()).then((action) => {
      if (fetchCart.fulfilled.match(action)) {
        notifyCartStockAdjustments(action.payload?.stock_adjustments, (msg) => toast.info(msg));
      }
    });
    dispatch(getPointsBalance());
  }, [dispatch]);

  useEffect(() => {
    const { firstName: fn, lastName: ln } = splitUserName(user);
    setFirstName(fn);
    setLastName(ln);
    setNameConfirmed(Boolean(fn && ln));

    // Prefill address while unconfirmed; do not clear confirmation after Save updates `user`.
    if (!addressConfirmed) {
      const initialAddress = user?.shipping_address || '';
      setShippingAddress(initialAddress);
      setDisplayShippingAddress(initialAddress);
      if (user?.latitude != null && user?.longitude != null) {
        setAddressCoords({ latitude: user.latitude, longitude: user.longitude });
      }
    }
  }, [user, addressConfirmed]);

  useEffect(() => {
    let cancelled = false;
    const resolveDisplayAddress = async () => {
      const coords = parseCoordinateAddress(shippingAddress);
      if (!coords) {
        setDisplayShippingAddress(shippingAddress);
        return;
      }
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
      const formatted = await reverseGeocodeAddress(coords, apiKey);
      if (cancelled) return;
      if (formatted) {
        setShippingAddress(formatted);
        setDisplayShippingAddress(formatted);
      } else {
        setDisplayShippingAddress('Address available on map');
      }
    };
    resolveDisplayAddress();
    return () => {
      cancelled = true;
    };
  }, [shippingAddress]);

  useEffect(() => {
    trackCheckoutStarted();
  }, []);

  useEffect(() => {
    setRemovalRecommendationDismissed(false);
  }, [cartFeeFingerprint]);

  useEffect(() => {
    if (!addressConfirmed || !items?.length) {
      setFeesLoading(false);
      setFeeConfigOnline(null);
      setFeeConfigInstapay(null);
      setFeesError(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setFeesLoading(true);
      setFeesError(null);
      try {
        const feeParams = { use_wallet_for_points: useWalletForPoints };
        const [onlineResult, instapayResult] = await Promise.allSettled([
          ordersAPI.getCheckoutFees({ ...feeParams, fee_payment_method: 'card' }),
          ordersAPI.getCheckoutFees({ ...feeParams, fee_payment_method: 'instapay' }),
        ]);

        if (cancelled) return;

        const onlineData = onlineResult.status === 'fulfilled' ? onlineResult.value : null;
        const instapayData = instapayResult.status === 'fulfilled' ? instapayResult.value : null;

        setFeeConfigOnline(onlineData);
        setFeeConfigInstapay(instapayData);

        if (!onlineData && !instapayData) {
          const err =
            onlineResult.status === 'rejected'
              ? onlineResult.reason
              : instapayResult.status === 'rejected'
                ? instapayResult.reason
                : null;
          setFeesError(
            err?.response?.data?.error
            || err?.message
            || 'Could not load delivery and processing fees.'
          );
        }
      } catch (e) {
        if (!cancelled) {
          setFeeConfigOnline(null);
          setFeeConfigInstapay(null);
          setFeesError(e?.message || 'Could not load delivery and processing fees.');
        }
      } finally {
        if (!cancelled) setFeesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    items?.length,
    cartFeeFingerprint,
    useWalletForPoints,
    user?.latitude,
    user?.longitude,
    addressConfirmed,
    feesFetchKey,
  ]);

  const handleApplyRemovalRecommendation = async () => {
    const rec = feeConfigOnline?.delivery_removal_recommendation;
    const ids = rec?.cart_item_ids_to_remove;
    if (!ids?.length) return;
    setRemovingRecommendedLines(true);
    try {
      for (const id of ids) {
        await cartAPI.removeFromCart(id);
      }
      await dispatch(fetchCart());
      toast.success('Cart updated — delivery fee refreshed.');
    } catch (_e) {
      toast.error('Could not remove those items. Try again.');
    } finally {
      setRemovingRecommendedLines(false);
    }
  };

  useEffect(() => {
    const pending = sessionStorage.getItem(PENDING_FEE_INTENT_KEY);
    if (!pending) {
      completionLockRef.current = false;
      return undefined;
    }
    if (completionLockRef.current) return undefined;
    completionLockRef.current = true;
    setCompletingAfterFees(true);

    let cancelled = false;
    const intentId = parseInt(pending, 10);
    if (Number.isNaN(intentId)) {
      sessionStorage.removeItem(PENDING_FEE_INTENT_KEY);
      sessionStorage.removeItem(POST_CHECKOUT_CART_SNAPSHOT_KEY);
      completionLockRef.current = false;
      setCompletingAfterFees(false);
      return undefined;
    }

    (async () => {
      const maxAttempts = 25;
      for (let i = 0; i < maxAttempts; i++) {
        if (cancelled) return;
        try {
          const result = await ordersAPI.completeOrderAfterFees(intentId);
          if (cancelled) return;
          sessionStorage.removeItem(PENDING_FEE_INTENT_KEY);
          let linesForReco = items || [];
          try {
            const snap = sessionStorage.getItem(POST_CHECKOUT_CART_SNAPSHOT_KEY);
            sessionStorage.removeItem(POST_CHECKOUT_CART_SNAPSHOT_KEY);
            if (snap) {
              const parsed = JSON.parse(snap);
              if (Array.isArray(parsed) && parsed.length) linesForReco = parsed;
            }
          } catch (_e) {
            sessionStorage.removeItem(POST_CHECKOUT_CART_SNAPSHOT_KEY);
          }
          trackOrderCompleted(result);
          linesForReco.forEach((lineItem) => {
            if (lineItem.customization_request_id) {
              trackPaintingCustomization('painting_request_converted_to_order', {
                request_id: lineItem.customization_request_id,
                catalog_item_id: lineItem.listing_id,
                order_id: result?.order_id,
                amount_cents: Math.round(Number(lineItem.price_at_time ?? lineItem.price ?? 0) * 100),
                state: 'converted',
              });
            }
          });
          // Fire one item_sold event per line item so PostHog can aggregate seller activity by month
          linesForReco.forEach(lineItem => {
            const itemType = lineItem.line_kind === 'designer'
              ? 'designer_item'
              : lineItem.line_kind === 'boutique'
                ? 'boutique_item'
                : 'item';
            trackItemSold({
              seller_id: String(lineItem.seller_id ?? ''),
              item_id: String(lineItem.listing_id ?? ''),
              item_type: itemType,
              price: parseFloat(lineItem.price_at_time ?? lineItem.price ?? 0),
            });
          });
          try {
            const recoPayload = await buildPostCheckoutSellerCatalogRecommendations(
              linesForReco,
              itemsAPI.getPublicUserItems,
            );
            if (recoPayload?.sellers?.length) {
              sessionStorage.setItem(POST_CHECKOUT_SELLER_CATALOG_KEY, JSON.stringify(recoPayload));
            }
          } catch (_e) {
            // Non-blocking: orders page works without recommendations.
          }
          await dispatch(getPointsBalance());
          orderJustCompletedRef.current = true;
          dispatch(clearCart());
          toast.success('Order placed successfully!');
          setCompletingAfterFees(false);
          completionLockRef.current = false;
          navigate('/orders');
          return;
        } catch (err) {
          const code = err?.response?.data?.code;
          if (code === 'FEES_NOT_PAID' && i < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, 2000));
            continue;
          }
          toast.error(err?.response?.data?.error || 'Could not complete order after payment.');
          sessionStorage.removeItem(POST_CHECKOUT_CART_SNAPSHOT_KEY);
          setCompletingAfterFees(false);
          completionLockRef.current = false;
          return;
        }
      }
      toast.error('Payment confirmation timed out. Open checkout again to retry.');
      sessionStorage.removeItem(POST_CHECKOUT_CART_SNAPSHOT_KEY);
      setCompletingAfterFees(false);
      completionLockRef.current = false;
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, navigate]);

  useEffect(() => {
    const refreshProfile = async () => {
      try {
        const profile = await authAPI.getUserProfile();
        const { firstName: fn, lastName: ln } = splitUserName(profile);
        if (fn && ln) {
          setFirstName(fn);
          setLastName(ln);
          setNameConfirmed(true);
        }
        if (profile?.shipping_address) {
          setShippingAddress((prev) => prev || profile.shipping_address);
          setDisplayShippingAddress((prev) => prev || profile.shipping_address);
        }
        if (profile?.latitude != null && profile?.longitude != null) {
          setAddressCoords((prev) => (
            prev.latitude != null && prev.longitude != null
              ? prev
              : { latitude: profile.latitude, longitude: profile.longitude }
          ));
        }
      } catch (_error) {
        // Keep checkout resilient if profile refresh fails.
      }
    };
    refreshProfile();
  }, []);

  useEffect(() => {
    if (orderError) {
      toast.error(orderError.error || 'Checkout failed');
    }
  }, [orderError]);

  // Redirect if cart is empty (skip right after a successful checkout clears the cart)
  useEffect(() => {
    if (orderJustCompletedRef.current) return;
    if (!cartLoading && items.length === 0 && !checkingOut && !completingAfterFees) {
      toast.info('Your cart is empty');
      navigate('/cart');
    }
  }, [items.length, cartLoading, navigate, checkingOut, completingAfterFees]);

  const handleProceedToCheckout = () => {
    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your first and last name before checkout.');
      return;
    }
    if (!nameConfirmed) {
      toast.error('Please confirm your name before checkout.');
      return;
    }
    if (!shippingAddress.trim()) {
      toast.error('Please add a shipping address before checkout.');
      return;
    }
    if (!addressConfirmed) {
      toast.error('Please confirm your shipping address before checkout.');
      return;
    }

    handleCheckout();
  };

  const handleSaveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter both first and last name.');
      return;
    }
    setSavingName(true);
    try {
      const response = await authAPI.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      dispatch(updateUser(response));
      setNameConfirmed(true);
      toast.success('Name saved.');
    } catch (_error) {
      toast.error('Failed to save name. Please try again.');
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!shippingAddress.trim()) {
      toast.error('Please enter your shipping address.');
      return;
    }
    setSavingAddress(true);
    try {
      const payload = { shipping_address: shippingAddress.trim() };
      let lat = addressCoords?.latitude;
      let lng = addressCoords?.longitude;
      if (lat == null || lng == null) {
        const parsed = parseCoordinateAddress(shippingAddress);
        if (parsed) {
          lat = parsed.latitude;
          lng = parsed.longitude;
        }
      }
      if (lat != null && lng != null) {
        payload.latitude = lat;
        payload.longitude = lng;
        setAddressCoords({ latitude: lat, longitude: lng });
      } else {
        toast.error('Please set your location on the map so we can calculate delivery fees.');
        setSavingAddress(false);
        return;
      }
      const response = await authAPI.updateProfile(payload);
      dispatch(updateUser(response));
      if (response?.latitude != null && response?.longitude != null) {
        setAddressCoords({
          latitude: Number(response.latitude),
          longitude: Number(response.longitude),
        });
      }
      // Confirm only after profile (incl. coords) is persisted — then refetch fees.
      setAddressConfirmed(true);
      setFeesFetchKey((k) => k + 1);
      toast.success('Shipping address saved. Updating delivery fees…');
    } catch (error) {
      toast.error('Failed to save shipping address. Please try again.');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleCheckout = async () => {
    setCheckingOut(true);

    try {
      const effectiveUseWalletForPoints = useWalletForPoints;
      const checkoutPricing = deriveCheckoutPricing({
        itemsSubtotal: Number(total) || 0,
        walletBalance: Number(balance) || 0,
        useWalletForPoints: effectiveUseWalletForPoints,
        feeConfigOnline,
        feeConfigInstapay,
        feePaymentMethod,
      });

      const walletCoversOrder = checkoutPricing.selectedPaymentAmount <= 0;

      const orderData = {
        fee_payment_method: feePaymentMethod,
        use_wallet_for_points: effectiveUseWalletForPoints,
      };
      if (feePaymentMethod !== 'instapay' && checkoutPricing.onlineFinalDue > 0) {
        orderData.online_payment_amount_egp = Number(checkoutPricing.onlineFinalDue).toFixed(2);
      }
      const data = await ordersAPI.initOrderFees(orderData, {
        freshAttempt: !sessionStorage.getItem(PENDING_FEE_INTENT_KEY),
      });
      try {
        sessionStorage.setItem(
          POST_CHECKOUT_CART_SNAPSHOT_KEY,
          JSON.stringify(snapshotCartLinesForPostCheckoutReco(items)),
        );
      } catch (_e) {
        // Snapshot is best-effort for post-checkout catalogue prompts.
      }

      if (feePaymentMethod === 'instapay' && !walletCoversOrder) {
        const instapayPayload = {
          intentId: data?.intent_id,
          orderReference: data?.order_number || data?.order_id || `#${data?.intent_id}`,
          amountDueEgp: Number(checkoutPricing.instapayFinalDue || 0),
          walletAppliedEgp: Number(checkoutPricing.walletAppliedTotalInstapay || 0),
          totalBeforeWalletEgp: Number(checkoutPricing.totalBeforeWalletInstapay || 0),
          qrUrl: INSTAPAY_QR_URL,
          supportWhatsApp: '+201040895962',
        };
        sessionStorage.setItem(INSTAPAY_PENDING_KEY, JSON.stringify(instapayPayload));
        navigate('/checkout/instapay', { state: instapayPayload });
        setCheckingOut(false);
        return;
      }

      const isWalletOnly = Boolean(data?.wallet_only) || checkoutPricing.selectedPaymentAmount <= 0;
      if (isWalletOnly) {
        setCompletingAfterFees(true);
        const result = await ordersAPI.completeOrderAfterFees(data.intent_id);
        sessionStorage.removeItem(PENDING_FEE_INTENT_KEY);
        sessionStorage.removeItem(POST_CHECKOUT_CART_SNAPSHOT_KEY);
        trackOrderCompleted(result);
        (items || []).forEach((lineItem) => {
          if (lineItem.customization_request_id) {
            trackPaintingCustomization('painting_request_converted_to_order', {
              request_id: lineItem.customization_request_id,
              catalog_item_id: lineItem.listing_id,
              order_id: result?.order_id,
              amount_cents: Math.round(Number(lineItem.price_at_time ?? lineItem.price ?? 0) * 100),
              state: 'converted',
            });
          }
        });
        (items || []).forEach((lineItem) => {
          const itemType = lineItem.line_kind === 'designer'
            ? 'designer_item'
            : lineItem.line_kind === 'boutique'
              ? 'boutique_item'
              : 'item';
          trackItemSold({
            seller_id: String(lineItem.seller_id ?? ''),
            item_id: String(lineItem.listing_id ?? ''),
            item_type: itemType,
          });
        });
        await dispatch(getPointsBalance());
        orderJustCompletedRef.current = true;
        dispatch(clearCart());
        toast.success('Order completed — fees paid from your wallet.');
        navigate('/orders');
        setCheckingOut(false);
        setCompletingAfterFees(false);
        return;
      }

      const checkoutUrl = resolvePaymobCheckoutUrl(data);
      if (!checkoutUrl) {
        throw new Error('Missing payment checkout link.');
      }

      sessionStorage.setItem(PENDING_FEE_INTENT_KEY, String(data.intent_id));
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      const msg = error?.response?.data?.error || error?.error || 'Checkout failed. Please try again.';
      const messageText = typeof msg === 'string' ? msg : 'Checkout failed. Please try again.';
      toast.error(messageText);
      setCheckingOut(false);
      setCompletingAfterFees(false);
    }
  };

  if (cartLoading || pointsLoading) {
    return <LoadingSpinner text="Loading checkout..." />;
  }

  if (completingAfterFees) {
    return <LoadingSpinner text="Confirming payment and placing your order..." />;
  }

  if (items.length === 0) {
    return null; // Will redirect
  }

  const cartItemsCount = items.reduce((sum, line) => {
    const parsedQuantity = Number(line?.quantity ?? 1);
    const quantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
    return sum + quantity;
  }, 0);
  const orderItemsCountFromApi = feeConfigOnline?.order_items_count;
  const orderItemsCount = typeof orderItemsCountFromApi === 'number' && orderItemsCountFromApi > 0
    ? orderItemsCountFromApi
    : cartItemsCount;
  const itemsSubtotal = Number(total) || 0;
  const pricing = deriveCheckoutPricing({
    itemsSubtotal,
    walletBalance: Number(balance) || 0,
    useWalletForPoints,
    feeConfigOnline,
    feeConfigInstapay,
    feePaymentMethod,
  });
  const {
    deliveryFee,
    processingFee,
    itemsFromWallet,
    walletAppliedToFeesOnline,
    walletAppliedToFeesInstapay,
    onlineFinalDue,
    instapayFinalDue,
    selectedPaymentAmount,
  } = pricing;
  const feeConfigReady = pricing.hasFeeConfig;
  const fullOrderTotal = itemsSubtotal + pricing.onlineFeesTotal;

  return (
    <CheckoutContainer>
      <PageTitle>Checkout</PageTitle>
      <DeliveryNotice>
        <DeliveryNoticeIcon aria-hidden>🚚</DeliveryNoticeIcon>
        <DeliveryNoticeBody>
          <DeliveryNoticeTitle>Per-order fees, not per item</DeliveryNoticeTitle>
          <DeliveryNoticeText>
            Delivery varies based on distance and number of items in your cart. We tried to keep
            costs as low as possible for orders.
          </DeliveryNoticeText>
        </DeliveryNoticeBody>
      </DeliveryNotice>
      
      <CheckoutContent>
        <OrderSummary>
          <SummaryTitle>Your Name</SummaryTitle>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
            Please confirm your name for delivery. Update the fields below if anything has changed.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <TextArea
              as="input"
              type="text"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setNameConfirmed(false);
              }}
              placeholder="First name *"
              style={{ minHeight: 'auto', padding: '0.75rem' }}
            />
            <TextArea
              as="input"
              type="text"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setNameConfirmed(false);
              }}
              placeholder="Last name *"
              style={{ minHeight: 'auto', padding: '0.75rem' }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              checked={nameConfirmed}
              onChange={(e) => setNameConfirmed(e.target.checked)}
              disabled={!firstName.trim() || !lastName.trim()}
            />
            Confirm this is my name
          </label>
          <CheckoutButton
            type="button"
            onClick={handleSaveName}
            disabled={savingName || !firstName.trim() || !lastName.trim()}
            style={{ marginBottom: '0.25rem' }}
          >
            {savingName ? 'Saving Name...' : 'Save Name'}
          </CheckoutButton>
        </OrderSummary>

        <OrderSummary>
          <SummaryTitle>Shipment Address</SummaryTitle>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
            Confirm your delivery location first. Delivery fees are calculated from this address.
          </p>
          {shippingAddress.trim() ? (
            <>
              <div style={{
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                padding: '0.875rem',
                marginBottom: '0.75rem',
                color: 'var(--text-primary)'
              }}>
                {displayShippingAddress || 'Address not available'}
              </div>
              <TextArea
                value={parseCoordinateAddress(shippingAddress) ? (displayShippingAddress || 'Address available on map') : shippingAddress}
                onChange={(e) => {
                  setShippingAddress(e.target.value);
                  setAddressConfirmed(false);
                }}
                placeholder="Update shipment address if needed"
              />
              <LocationPicker
                value={shippingAddress}
                onChange={(address, coords) => {
                  setShippingAddress(address);
                  setAddressConfirmed(false);
                  if (coords?.latitude != null && coords?.longitude != null) {
                    setAddressCoords({ latitude: coords.latitude, longitude: coords.longitude });
                  }
                }}
              />
            </>
          ) : (
            <>
              <div style={{ color: 'var(--error-color)', marginBottom: '0.75rem', fontWeight: 600 }}>
                No shipment address found. Please add one to continue.
              </div>
              <TextArea
                value={parseCoordinateAddress(shippingAddress) ? (displayShippingAddress || 'Address available on map') : shippingAddress}
                onChange={(e) => {
                  setShippingAddress(e.target.value);
                  setAddressConfirmed(false);
                }}
                placeholder="Enter shipment address"
              />
              <LocationPicker
                value={shippingAddress}
                onChange={(address, coords) => {
                  setShippingAddress(address);
                  setAddressConfirmed(false);
                  if (coords?.latitude != null && coords?.longitude != null) {
                    setAddressCoords({ latitude: coords.latitude, longitude: coords.longitude });
                  }
                }}
              />
            </>
          )}
          <CheckoutButton
            type="button"
            onClick={handleSaveAddress}
            disabled={savingAddress || !shippingAddress.trim()}
            style={{ marginTop: '0.75rem' }}
          >
            {savingAddress
              ? 'Saving Address...'
              : addressConfirmed
                ? 'Address confirmed — update if needed'
                : 'Save & confirm shipment address'}
          </CheckoutButton>
          {shippingAddress.trim() ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
              <input
                type="checkbox"
                checked={addressConfirmed}
                onChange={(e) => {
                  if (e.target.checked) {
                    handleSaveAddress();
                  } else {
                    setAddressConfirmed(false);
                  }
                }}
                disabled={savingAddress || !shippingAddress.trim()}
              />
              Confirm this is my shipment address
            </label>
          ) : null}
        </OrderSummary>

        {!addressConfirmed ? (
          <OrderSummary>
            <SummaryTitle>Order Summary</SummaryTitle>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Confirm your shipment address above to see delivery fees and your order total.
            </p>
          </OrderSummary>
        ) : (
          <>
        <OrderSummary>
          <SummaryTitle>Pay sellers from your wallet</SummaryTitle>
          <WalletToggleCard>
            <WalletToggleMain>
              <WalletIconWrap aria-hidden>👛</WalletIconWrap>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Pay for items with wallet</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                  Your wallet · {formatWalletBalanceEgp(balance)}
                </div>
              </div>
            </WalletToggleMain>
            <label style={{ cursor: Number(balance) > 0 ? 'pointer' : 'not-allowed', flexShrink: 0 }}>
              <input
                type="checkbox"
                checked={useWalletForPoints}
                disabled={Number(balance) <= 0}
                onChange={(e) => setUseWalletForPoints(e.target.checked)}
                style={{ width: '1.35rem', height: '1.35rem', accentColor: 'var(--primary-color)' }}
              />
            </label>
          </WalletToggleCard>
          {Number(balance) <= 0 ? (
            <FeePayHint>Add funds to your wallet to apply wallet deduction at checkout.</FeePayHint>
          ) : null}
          {useWalletForPoints && Number(balance) > 0 && Number(balance) < fullOrderTotal ? (
            <WalletCoverageWarning>
              Wallet balance does not fully cover items and fees. You will pay the remaining amount at checkout.
            </WalletCoverageWarning>
          ) : null}
        </OrderSummary>

        <OrderSummary>
          <SummaryTitle>Order Summary</SummaryTitle>
          
          <SummaryRow>
            <span>Items ({orderItemsCount})</span>
            <span>Listed below</span>
          </SummaryRow>

          {items.length > 0 ? (
            <div style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
                Cart lines
              </div>
              {items.map((line) => {
                const qty = Number(line?.quantity ?? 1);
                const q = Number.isFinite(qty) && qty > 0 ? qty : 1;
                const price = parseFloat(line.price_at_time ?? line.price ?? 0);
                const lineTotal = Number.isFinite(price) ? price * q : 0;
                return (
                  <SummaryRow key={line.cart_item_id} style={{ fontSize: '0.875rem' }}>
                    <span style={{ paddingRight: '0.5rem' }}>
                      {line.title || 'Item'}
                      {q > 1 ? ` × ${q}` : ''}
                    </span>
                    <span>{formatItemPriceEgp(lineTotal)}</span>
                  </SummaryRow>
                );
              })}
            </div>
          ) : null}

          {feeConfigOnline?.delivery_removal_recommendation &&
          orderItemsCount > 1 &&
          !removalRecommendationDismissed ? (
            <RemovalRecommendationCard>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.35rem' }}>
                Delivery tip
              </div>
              <div>{buildDeliveryRemovalMessage(feeConfigOnline.delivery_removal_recommendation, items)}</div>
              <RemovalRecommendationActions>
                <RemovalSecondaryButton
                  type="button"
                  onClick={() => setRemovalRecommendationDismissed(true)}
                >
                  Dismiss
                </RemovalSecondaryButton>
                <RemovalPrimaryButton
                  type="button"
                  disabled={removingRecommendedLines}
                  onClick={handleApplyRemovalRecommendation}
                >
                  {removingRecommendedLines ? 'Removing…' : 'Remove from cart'}
                </RemovalPrimaryButton>
              </RemovalRecommendationActions>
            </RemovalRecommendationCard>
          ) : null}
          
          <SummaryRow className="total">
            <span>Items total</span>
            <span>{formatItemPriceEgp(itemsSubtotal)}</span>
          </SummaryRow>
          {feesLoading ? (
            <SummaryRow>
              <span>Delivery &amp; fees</span>
              <span>Calculating…</span>
            </SummaryRow>
          ) : feeConfigReady ? (
            <>
              <SummaryRow>
                <span>Delivery fee</span>
                <span>{formatItemPriceEgp(deliveryFee)}</span>
              </SummaryRow>
              {feePaymentMethod !== 'instapay' && processingFee > 0 ? (
                <SummaryRow>
                  <span>Processing fee</span>
                  <span>{formatItemPriceEgp(processingFee)}</span>
                </SummaryRow>
              ) : null}
              {useWalletForPoints && itemsFromWallet > 0 ? (
                <SummaryRow>
                  <span>Items paid from wallet</span>
                  <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>
                    −{formatItemPriceEgp(itemsFromWallet)}
                  </span>
                </SummaryRow>
              ) : null}
              {useWalletForPoints && walletAppliedToFeesInstapay > 0 ? (
                <SummaryRow>
                  <span>Wallet applied to fees (Instapay)</span>
                  <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>
                    −{formatItemPriceEgp(walletAppliedToFeesInstapay)}
                  </span>
                </SummaryRow>
              ) : null}
              <SummaryRow className="total">
                <span>Total due with Instapay</span>
                <span>{formatItemPriceEgp(instapayFinalDue)}</span>
              </SummaryRow>
              {feePaymentMethod !== 'instapay' ? (
                <>
                  {useWalletForPoints && walletAppliedToFeesOnline > 0 ? (
                    <SummaryRow>
                      <span>Wallet applied to fees (online)</span>
                      <span style={{ color: 'var(--primary-color)', fontWeight: 600 }}>
                        −{formatItemPriceEgp(walletAppliedToFeesOnline)}
                      </span>
                    </SummaryRow>
                  ) : null}
                  <SummaryRow className="total">
                    <span>Total due online (card / Apple Pay / Google Pay / Valu / cash)</span>
                    <span>{formatItemPriceEgp(onlineFinalDue)}</span>
                  </SummaryRow>
                </>
              ) : null}
            </>
          ) : (
            <SummaryRow>
              <span>Delivery &amp; fees</span>
              <span>
                {feesError ? (
                  <button
                    type="button"
                    onClick={() => setFeesFetchKey((k) => k + 1)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary-color)',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: 0,
                      font: 'inherit',
                    }}
                  >
                    Tap to retry
                  </button>
                ) : (
                  'Unavailable'
                )}
              </span>
            </SummaryRow>
          )}
          {feesError && !feesLoading ? (
            <p style={{ fontSize: '0.8125rem', color: '#b45309', margin: '0.5rem 0 0' }}>
              {typeof feesError === 'string' ? feesError : 'Could not load fees. Please retry.'}
            </p>
          ) : null}
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: '0.75rem 0 0', lineHeight: 1.5 }}>
            Wallet deduction is applied instantly when enabled and removed instantly when disabled.
          </p>
        </OrderSummary>

        <OrderSummary>
          <SummaryTitle>Payment method for fees</SummaryTitle>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
            Instapay is available now. Other online payment methods are coming soon.
          </p>
          <PaymentMethodsGrid>
            {FEE_PAYMENT_OPTIONS.map((opt) => {
              const isInstapay = opt.id === 'instapay';
              return (
              <DeliveryOption
                key={opt.id}
                type="button"
                selected={feePaymentMethod === opt.id}
                $dimmed={!isInstapay}
                onClick={() => {
                  if (!isInstapay) {
                    toast.info('Online payment coming soon');
                    return;
                  }
                  setFeePaymentMethod(opt.id);
                }}
              >
                <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ fontSize: '1.125rem', lineHeight: 1 }} aria-hidden>{opt.icon}</div>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{opt.label}</div>
                </div>
                {feePaymentMethod === opt.id ? <Checkmark>✓</Checkmark> : <div style={{ width: 28, flexShrink: 0 }} />}
              </DeliveryOption>
              );
            })}
          </PaymentMethodsGrid>
          <FeePayHint>
            Pay with Instapay to skip the processing fee. Card, Google Pay, Apple Pay, Valu, and cash are coming soon.
          </FeePayHint>
        </OrderSummary>
          </>
        )}

        <div>
          
          <CheckoutButton
            onClick={handleProceedToCheckout}
            disabled={
              checkingOut
              || !firstName.trim()
              || !lastName.trim()
              || !nameConfirmed
              || !shippingAddress.trim()
              || !addressConfirmed
            }
          >
            {checkingOut
              ? (selectedPaymentAmount <= 0 ? 'Completing order…' : 'Opening Paymob…')
              : feeConfigReady
                ? selectedPaymentAmount <= 0
                  ? 'Complete order'
                  : feePaymentMethod === 'instapay'
                    ? `Pay ${formatItemPriceEgp(selectedPaymentAmount)} with Instapay`
                    : `Pay ${formatItemPriceEgp(selectedPaymentAmount)} online`
                : `Complete order (${formatItemPriceEgp(total)})`}
          </CheckoutButton>
          
          <BackButton onClick={() => navigate('/cart')}>
            Back to Cart
          </BackButton>
        </div>
      </CheckoutContent>

    </CheckoutContainer>
  );
};

export default CheckoutPage;

